import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import { PHOTOS } from "@/data";
import { useLang } from "@/i18n";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.15 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={PHOTOS.living} alt="Luxury painted living room" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-[#050505]/60" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-[#d4af37]" />
          <span className="text-[#d4af37] uppercase tracking-[0.3em] text-xs flex items-center gap-2">
            <Sparkles size={13} /> {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-serif-lux font-light leading-[0.92] tracking-tighter text-white text-5xl sm:text-7xl lg:text-[8rem]">
          <span className="mask-line"><motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">{t.hero.title}</motion.span></span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-white/70 text-base sm:text-lg font-light leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-11 flex flex-wrap items-center gap-5"
        >
          <button
            onClick={() => scrollTo("contact")}
            data-testid="hero-cta-button"
            className="group relative overflow-hidden bg-[#d4af37] text-black px-9 py-4 uppercase tracking-[0.15em] text-sm font-medium gold-glow"
          >
            <span className="relative z-10">{t.hero.cta}</span>
            <span className="absolute inset-0 bg-[#f1e5ac] translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out" />
          </button>
          <button
            onClick={() => scrollTo("portfolio")}
            data-testid="hero-secondary-button"
            className="border border-white/25 text-white px-9 py-4 uppercase tracking-[0.15em] text-sm hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300"
          >
            {t.hero.secondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
